import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Reusable custom checkbox. Renders the shared `.chk-container` markup
 * (see src/scss/_inputs.scss) so styling stays in one place instead of
 * being hand-written at every call site.
 *
 * Form usage: `formControlName`, `[formControl]` or `[(ngModel)]` (CVA).
 * Standalone usage: `[checked]` + `(changeEvent)` — the raw change event is
 * re-emitted so handlers reading $event.target.checked/.value keep working.
 *
 * Custom checkmark content (e.g. the offer-characteristics icons) can be
 * projected: <img checkmark-content ... /> renders inside the checkmark span.
 */
@Component({
  selector: 'app-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
})
export class CheckboxComponent implements ControlValueAccessor, OnInit {
  @Input() label = '';
  @Input() inputId = '';
  @Input() disabled = false;
  @Input() helpText = '';
  /** Optional tooltip shown as a title on a `?` marker beside the label. */
  @Input() tooltip = '';
  @Input() isInvalid = false;
  @Input() errorText = '';
  /** Extra class(es) on the .chk-container label, e.g. 'col-sm-3'. */
  @Input() labelClass = '';
  /** Optional value/name attributes (handlers reading $event.target.value). */
  @Input() nativeValue: string | null = null;
  @Input() name: string | null = null;
  /** Optional data-testid on the label for e2e targeting. */
  @Input() testId: string | null = null;
  /** Stop click bubbling (checkboxes inside drag/expand rows). */
  @Input() stopClickPropagation = false;
  /** Indeterminate visual state (select-all headers over a partial selection). */
  @Input() indeterminate = false;
  /** Vertically center the checkmark against tall/rich labels (logo rows). */
  @Input() centerCheckmark = false;
  /** Muted gray checked state for illustrative example checkboxes
   *  (the option-group type demos) instead of the brand color. */
  @Input() muted = false;
  /** Compact geometry for dense rows (an 18px box with a 14px tick instead of
   *  the default 25px/20px), so a caller does not have to reach in with
   *  ::ng-deep and magic numbers to shrink one. */
  @Input() size: 'sm' | 'md' = 'md';
  /** Visual-only checkbox: the surrounding row owns both the state and the
   *  interaction, so the input is inert to pointers, not focusable and hidden
   *  from assistive tech (which would otherwise read a second, contradictory
   *  control). Without this a call site has to fight the hit-testing from
   *  outside and still leaves a tab stop that can toggle the box with Space
   *  while nothing handles it. */
  @Input() presentational = false;
  /** Set when label content is projected (no `label` key) so the bare-checkbox
   *  geometry (`chk-container--bare`) is not applied. */
  @Input() richLabel = false;
  /** Standalone (non-form) usage: drive the checked state from the parent. */
  @Input() set checked(v: boolean | null | undefined) {
    if (v !== null && v !== undefined) {
      this.value = v;
    }
  }

  /** Standalone usage: the raw change event (target.checked / target.value). */
  @Output() changeEvent = new EventEmitter<Event>();

  value = false;

  private onChange: (value: boolean) => void = () => {};
  onTouched: () => void = () => {};

  ngOnInit(): void {
    if (!this.inputId) {
      this.inputId = 'chk-' + Math.random().toString(36).substring(2, 11);
    }
  }

  writeValue(value: boolean): void {
    this.value = !!value;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onClick(event: Event): void {
    if (this.stopClickPropagation) {
      event.stopPropagation();
    }
  }

  onNativeChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.value = checked;
    this.onChange(checked);
    this.onTouched();
    this.changeEvent.emit(event);
  }
}
